import { JOB_STATUS, PROJECT_MODE, PROJECT_VIEW_MODE } from './constants';
import { buildProjectUrl } from './projectPath';

/**
 * returns the job name based on the job type
 * @param {string} type The job type
 * @returns {string} Returns the job name
 */
export const getJobName = type => {
	switch (type) {
		case 'segmentation_train':
			return 'Segmentation training';
		case 'segmentation_predict':
			return 'Segmentation prediction';
		case 'object_detection_train':
			return 'Object detection training';
		case 'object_detection_predict':
			return 'Object detection prediction';
		case 'photogrammetry':
			return 'Photogrammetry';
		case 'grid_tiling':
			return 'Grid tiling';
		case 'reproject_tif':
			return 'Reprojecting TIF';
		case 'user_annotation_processing':
			return 'User annotation processing';
		case 'user_annotation_train':
			return 'User annotation training';
		case 'orthophoto_postprocessing':
			return 'Processing orthophoto';
		case 'segmentation_SAM':
			return 'Segmentation SAM';
		case 'model_evaluation':
			return 'Model evaluation';
		case 'single_image_processing':
			return 'Single image processing';

		default:
			return 'Job in progress';
	}
};

export const getNotificationInterval = mode => {
	switch (mode) {
		case PROJECT_MODE.SINGLE_IMAGE:
			return 5000; // 5 seconds
		default:
			return 1000 * 60; // 1 minute
	}
};

export const getJobTypeClassification = type => {
	switch (type) {
		case 'photogrammetry':
		case 'grid_tiling':
		case 'reproject_tif':
		case 'orthophoto_postprocessing':
		case 'single_image_processing':
			return 'Creating Project';

		case 'segmentation_train':
		case 'object_detection_train':
		case 'user_annotation_processing':
		case 'user_annotation_train':
			return 'Training';

		case 'segmentation_predict':
		case 'object_detection_predict':
		case 'segmentation_SAM':
			return 'Detecting';

		case 'model_evaluation':
			return 'Evaluating';

		default:
			return null;
	}
};

export const extractLatestNotificationJobProgress = message => {
	let lastProgress = null;

	// Iterate over each key in the message object
	for (const key in message) {
		// Check if the key is an own property and the value is an object with a 'progress' property
		if (
			Object.prototype.hasOwnProperty.call(message, key) &&
			typeof message[key] === 'object' &&
			Object.prototype.hasOwnProperty.call(message[key], 'progress')
		) {
			// Update lastProgress with the current job's details
			lastProgress = {
				jobType: key,
				jobName: getJobName(key),
				progress: message[key].progress,
				text: message[key].text,
				classification: getJobTypeClassification(key),
			};
		}
	}

	// Check if the last job is a project creation job
	const isProjectCreationJob = lastProgress.classification === 'Creating Project';

	// If the job is a project creation job and is completed, override the text
	if (isProjectCreationJob && lastProgress?.progress === 100) {
		lastProgress.text = 'Project created';
	}

	return lastProgress; // Return the last progress found or null if none found
};

/** Job types whose notification links to the model page rather than a project. */
const MODEL_PAGE_JOB_TYPES = new Set(['model_evaluation', 'user_annotation_train']);

/**
 * Resolve where a job notification should link to.
 *
 * Detection runs started from the collection-based (v2) flow carry a
 * `collection_uuid`, and their results only exist on the collection route, so
 * they link to `/projects/v2/:uuid?collection_uuid=`. The backend also sets
 * `model_uuid` on those runs, so the collection has to win over the model —
 * otherwise the link drops the user into model mode with an arbitrary model
 * preselected. Runs without a collection (legacy model-mode detections, single
 * image) keep linking to model mode as before.
 *
 * @param {object} params
 * @param {string} params.project_uuid
 * @param {string | null | undefined} params.model_uuid
 * @param {string | null | undefined} params.collection_uuid
 * @param {string | undefined} params.jobType The job type key from the message.
 * @returns {string} A route path, optionally with a query string.
 */
export const getJobNotificationUrl = ({
	project_uuid,
	project_mode,
	model_uuid,
	collection_uuid,
	jobType,
}) => {
	if (MODEL_PAGE_JOB_TYPES.has(jobType)) return `/models/${model_uuid}`;
	if (project_mode === PROJECT_MODE.VIDEO) return `/video-projects/${project_uuid}`;

	if (collection_uuid) {
		const base = buildProjectUrl(project_uuid, PROJECT_VIEW_MODE.COLLECTION);
		return `${base}?collection_uuid=${encodeURIComponent(collection_uuid)}`;
	}

	const base = buildProjectUrl(project_uuid, PROJECT_VIEW_MODE.MODEL);
	return model_uuid ? `${base}?model=${encodeURIComponent(model_uuid)}` : base;
};

/** Job statuses that mean the job is still going — the complement of TERMINAL_JOB_STATUSES. */
const ACTIVE_JOB_STATUSES = new Set([JOB_STATUS.RUNNING, JOB_STATUS.PENDING]);

/** True when a notification's job is still running or waiting to start. */
export const isActiveJobNotification = notification =>
	ACTIVE_JOB_STATUSES.has(notification?.job_status);

/**
 * Pick out the notifications whose job was active on the previous poll and has
 * since reached a terminal status. This is the "a job just finished" signal the
 * notifications poll can offer — there is no push — and it's what should
 * trigger a cache refresh of whatever that job produced.
 *
 * A notification that was active and is now gone from the list (deleted) is
 * not counted: nothing finished, so there is nothing to refresh.
 *
 * @param {Set<string>} previouslyActiveUuids Notification uuids that were active last time.
 * @param {Array<object>} notifications The current notifications list.
 * @returns {Array<object>} The notifications that went from active to terminal.
 */
export const getSettledJobNotifications = (previouslyActiveUuids, notifications = []) =>
	notifications.filter(
		notification =>
			previouslyActiveUuids.has(notification.uuid) && !isActiveJobNotification(notification)
	);

/**
 * The distinct models the given notifications ran against. Used to refresh the
 * model-keyed caches of exactly the models whose jobs finished, rather than
 * whichever model happens to be picked in the URL — those can differ when a
 * job for one model completes while the user is looking at another.
 *
 * @param {Array<object>} notifications
 * @returns {string[]} Unique, non-empty model uuids in first-seen order.
 */
export const getNotificationModelUuids = (notifications = []) => [
	...new Set(notifications.map(n => n.model_uuid).filter(Boolean)),
];
